import { Grid } from "@material-ui/core";
import { ViewEditMode } from "@saleor/changeOrderExport/views/ViewEditOrder/types";
import MyTypography from "@saleor/components/MyTypography";
import SingleAutocomplete from "@saleor/components/SingleAutocomplete";
import TextInputDisable from "@saleor/components/TextInputDisable";
import { ContractChangeOrderDomesticQuery } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { makeStyles } from "@saleor/macaw-ui";
import { FormikProps } from "formik";
import React from "react";
import { useIntl } from "react-intl";

interface SalesTabProps {
  formik: FormikProps<any>;
  dataOrder: ContractChangeOrderDomesticQuery["changeOrder"];
  saleOrganizationChoices: any[];
  distributionChannelChoices: any[];
  saleOfficeChoices: any[];
  saleGroupChoices: any[];
  divisionChoices: any[];
  orderTypeDesc?: string;
}

const useStyles = makeStyles(
  theme => ({
    boxBackground: {
      borderRadius: 4,
      background: theme.palette.background.default,
      padding: "9px 16px",
      height: 40
    },
    selectField: {
      height: 40
    },
    gridRow: {
      marginBottom: 15
    }
  }),
  { name: "SalesTab" }
);

const SalesTab: React.FC<SalesTabProps> = props => {
  const {
    formik,
    dataOrder,
    saleOrganizationChoices,
    saleGroupChoices,
    saleOfficeChoices,
    distributionChannelChoices,
    divisionChoices,
    orderTypeDesc
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const [{ viewEditMode }] = useAppState();

  return (
    <Grid container data-testid="salesTabDetail">
      <Grid item xs={12} md={4}>
        <Grid
          container
          spacing={2}
          alignItems={"center"}
          className={classes.gridRow}
        >
          <Grid item md={4}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({ defaultMessage: "Order Type" })}
            </MyTypography>
          </Grid>
          <Grid item md={8}>
            <TextInputDisable
              value={orderTypeDesc}
              testId="orderTypeDesc-readonlyInput"
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          alignItems={"center"}
          className={classes.gridRow}
        >
          <Grid item md={4}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({ defaultMessage: "Sales Organization" })}
            </MyTypography>
          </Grid>
          <Grid item md={8}>
            {viewEditMode === ViewEditMode.VIEW ? (
              <MyTypography
                variant={"body3"}
                className={classes.boxBackground}
                data-testid="salesOrgShortName-text"
              >
                {dataOrder?.salesOrgShortName
                  ? `${dataOrder?.salesOrgShortName}`
                  : ""}
              </MyTypography>
            ) : (
              <SingleAutocomplete
                disabled={true}
                name={"saleOrganization"}
                id="saleOrganization"
                data-testid="saleOrganization-select"
                options={saleOrganizationChoices}
                onChange={formik.handleChange}
                className={classes.selectField}
                value={formik.values.saleOrganization}
                getOptionLabel={option => option.label}
                placeholder={"Select"}
                error={
                  !!formik.errors.saleOrganization &&
                  !!formik.touched.saleOrganization
                }
              />
            )}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          alignItems={"center"}
          className={classes.gridRow}
        >
          <Grid item md={4}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({ defaultMessage: "Distribution Channel" })}
            </MyTypography>
          </Grid>
          <Grid item md={8}>
            {viewEditMode === ViewEditMode.VIEW ? (
              <MyTypography
                variant={"body3"}
                className={classes.boxBackground}
                data-testid="distributionChannel-text"
              >
                {dataOrder?.distributionChannel
                  ? `${dataOrder?.distributionChannel}-${dataOrder?.distributionChannelName}`
                  : ""}
              </MyTypography>
            ) : (
              <SingleAutocomplete
                disabled={true}
                value={formik.values.distributionChannel}
                options={distributionChannelChoices}
                onChange={formik.handleChange}
                className={classes.selectField}
                name={"distributionChannel"}
                id="distributionChannel"
                data-testid="distributionChannel-select"
                getOptionLabel={option => option.label}
                placeholder={"Select"}
                error={
                  !!formik.errors.distributionChannel &&
                  !!formik.touched.distributionChannel
                }
              />
            )}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          alignItems={"center"}
          className={classes.gridRow}
        >
          <Grid item md={4}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({ defaultMessage: "Division" })}
            </MyTypography>
          </Grid>
          <Grid item md={8}>
            {viewEditMode === ViewEditMode.VIEW ? (
              <MyTypography
                variant={"body3"}
                className={classes.boxBackground}
                data-testid="division-text"
              >
                {dataOrder?.division
                  ? `${dataOrder?.division}-${dataOrder?.divisionName}`
                  : ""}
              </MyTypography>
            ) : (
              <SingleAutocomplete
                disabled={true}
                value={formik.values.division}
                options={divisionChoices}
                onChange={formik.handleChange}
                className={classes.selectField}
                name={"division"}
                id="division"
                data-testid="division-select"
                getOptionLabel={option => option.label}
                placeholder={"Select"}
                error={!!formik.errors.division && !!formik.touched.division}
              />
            )}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          alignItems={"center"}
          className={classes.gridRow}
        >
          <Grid item md={4}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({ defaultMessage: "Sales Group" })}
            </MyTypography>
          </Grid>
          <Grid item md={8}>
            {viewEditMode === ViewEditMode.VIEW ? (
              <MyTypography
                variant={"body3"}
                className={classes.boxBackground}
                data-testid="salesGroup-text"
              >
                {dataOrder?.salesGroup
                  ? `${dataOrder?.salesGroup}-${dataOrder?.salesGroupName}`
                  : ""}
              </MyTypography>
            ) : (
              <SingleAutocomplete
                disabled={true}
                value={formik.values.saleGroup}
                options={saleGroupChoices}
                onChange={formik.handleChange}
                className={classes.selectField}
                name={"saleGroup"}
                id="saleGroup"
                data-testid="saleGroup-select"
                getOptionLabel={option => option.label}
                placeholder={"Select"}
                error={
                  !!formik.errors.salesGroup && !!formik.touched.salesGroup
                }
              />
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems={"center"}>
          <Grid item md={4}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({ defaultMessage: "Sales Office" })}
            </MyTypography>
          </Grid>
          <Grid item md={8}>
            {viewEditMode === ViewEditMode.VIEW ? (
              <MyTypography
                variant={"body3"}
                className={classes.boxBackground}
                data-testid="salesOff-text"
              >
                {dataOrder?.salesOff
                  ? `${dataOrder?.salesOff}-${dataOrder?.salesOffName}`
                  : ""}
              </MyTypography>
            ) : (
              <SingleAutocomplete
                disabled={true}
                value={formik.values.saleOffice}
                options={saleOfficeChoices}
                onChange={formik.handleChange}
                className={classes.selectField}
                name={"saleOffice"}
                id="saleOffice"
                data-testid="saleOffice-select"
                getOptionLabel={option => option.label}
                placeholder={"Select"}
                error={
                  !!formik.errors.saleOffice && !!formik.touched.saleOffice
                }
              />
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={7} />
    </Grid>
  );
};

export default SalesTab;
